import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, runSpkOcr } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, Trash2, Plus, FileText, Car, History } from "lucide-react";
import { WORK_TYPES, SUPPLY_BY, label, formatRupiah, recalcItem } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrandModelSelect } from "@/components/BrandModelSelect";

const EMPTY = {
  license_plate: "", customer_name: "", finance_name: "", company_customer: "", vehicle_brand: "", vehicle_model: "",
  insurance_company: "", spk_number: "", spk_date: "", spk_value: 0, jasa_items: [], spare_parts: [], spk_file: null, confidence: 0,
  insurance_claim_number: "", vin: "", customer_phone: "", assigned_sa: "",
};

export default function UploadSPKDialog({ onCreated, compact = false }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null); // {warnings, previous_visits}
  const [reason, setReason] = useState("");
  const [vehMatch, setVehMatch] = useState(null); // vehicle lookup result
  const [filledVid, setFilledVid] = useState("");
  const navigate = useNavigate();

  const reset = () => { setStep("upload"); setForm(EMPTY); setOcrLoading(false); setConfirm(null); setReason(""); setVehMatch(null); setFilledVid(""); };

  // Live Vehicle Master lookup (auto-detect + auto-fill) while reviewing a claim
  useEffect(() => {
    if (step !== "review") return;
    const plate = (form.license_plate || "").trim();
    const vin = (form.vin || "").trim();
    if (!plate && !vin) { setVehMatch(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/vehicles/lookup", { params: { plate, vin } });
        if (data.found) {
          setVehMatch(data);
          if (data.vehicle.vehicle_id !== filledVid) {
            setForm((f) => ({
              ...f,
              vehicle_brand: f.vehicle_brand || data.vehicle.brand || "",
              vehicle_model: f.vehicle_model || data.vehicle.model || "",
              customer_name: f.customer_name || data.vehicle.owner_name || "",
              vin: f.vin || data.vehicle.vin || "",
              customer_phone: f.customer_phone || data.vehicle.phone || "",
            }));
            setFilledVid(data.vehicle.vehicle_id);
          }
        } else { setVehMatch(null); }
      } catch (e) { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form.license_plate, form.vin, step, filledVid]);

  const handleFile = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    try {
      const data = await runSpkOcr(file);
      setForm({ ...EMPTY, ...data.data });
      if (data.success) toast.success("SPK extracted. Please review every field.");
      else toast.warning("Auto-extract failed — the document is attached; enter fields manually.");
      setStep("review");
    } catch (e) {
      setForm(EMPTY);
      toast.warning("Auto-extract unavailable. Enter fields manually.");
      setStep("review");
    } finally { setOcrLoading(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Jasa handlers
  const setJasa = (i, k, v) => setForm((f) => { const a = [...f.jasa_items]; a[i] = recalcItem({ ...a[i], [k]: v }); return { ...f, jasa_items: a }; });
  const addJasa = () => setForm((f) => ({ ...f, jasa_items: [...f.jasa_items, { work_description: "", work_type: "OTHER", qty: 1, price_list: 0, discount_pct: 0, approved_unit_price: 0, total_approved: 0 }] }));
  const delJasa = (i) => setForm((f) => ({ ...f, jasa_items: f.jasa_items.filter((_, x) => x !== i) }));

  // Part handlers
  const setPart = (i, k, v) => setForm((f) => { const a = [...f.spare_parts]; a[i] = recalcItem({ ...a[i], [k]: v }); return { ...f, spare_parts: a }; });
  const addPart = () => setForm((f) => ({ ...f, spare_parts: [...f.spare_parts, { part_name: "", part_number: "", quantity: 1, supply_by: "UNKNOWN", price_list: 0, discount_pct: 0, approved_unit_price: 0, total_approved: 0, order_status: "NOT_ORDERED", received_status: "NOT_RECEIVED" }] }));
  const delPart = (i) => setForm((f) => ({ ...f, spare_parts: f.spare_parts.filter((_, x) => x !== i) }));

  const approvedJasa = form.jasa_items.reduce((s, j) => s + Number(j.total_approved || 0), 0);
  const approvedParts = form.spare_parts.reduce((s, p) => s + Number(p.total_approved || 0), 0);
  const calcTotal = approvedJasa + approvedParts;
  const spkVal = Number(form.spk_value || 0);
  const diff = calcTotal - spkVal;
  const reconOk = spkVal === 0 || Math.abs(diff) < 1;

  const save = async (force = false) => {
    if (!form.license_plate.trim()) { toast.error("License plate is required"); return; }
    if (force && !reason.trim()) { toast.error("Alasan wajib diisi untuk membuat claim baru"); return; }
    setSaving(true);
    try {
      const payload = {
        license_plate: form.license_plate, customer_name: form.customer_name, finance_name: form.finance_name,
        company_customer: form.company_customer, vehicle_brand: form.vehicle_brand, vehicle_model: form.vehicle_model,
        insurance_company: form.insurance_company, spk_number: form.spk_number, spk_date: form.spk_date,
        spk_value: Number(form.spk_value) || 0, spk_file: form.spk_file,
        insurance_claim_number: form.insurance_claim_number || "", vin: form.vin || "", customer_phone: form.customer_phone || "", assigned_sa: form.assigned_sa || "",
        force, override_reason: reason,
        jasa_items: form.jasa_items.map((j, idx) => ({ ...recalcItem(j), line_no: idx + 1, qty: Number(j.qty) || 1 })),
        spare_parts: form.spare_parts.map((p, idx) => ({ ...recalcItem(p), line_no: idx + 1, quantity: Number(p.quantity) || 1, order_status: p.order_status || "NOT_ORDERED", received_status: p.received_status || "NOT_RECEIVED", supply_by: p.supply_by || "UNKNOWN" })),
      };
      const { data } = await api.post("/claims", payload);
      toast.success(`Claim ${data.claim_no || ""} dibuat · ${data.vehicle_id || ""}`);
      setOpen(false); reset();
      onCreated?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object" && d.code === "REQUIRE_CONFIRM") { setConfirm(d); }
      else toast.error(formatApiErrorDetail(d) || "Gagal membuat claim");
    } finally { setSaving(false); }
  };
  const activeWarn = confirm?.warnings?.find((w) => w.code === "ACTIVE_JOB");

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="upload-spk-btn" className={compact
          ? "h-10 rounded-lg border-2 border-[#20466D] bg-white px-3 text-[14px] font-bold text-[#20466D] hover:bg-[#E8EEF5]"
          : "bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]"}>
          <UploadCloud size={16} className={compact ? "mr-1.5" : "mr-2"} /> Upload SPK
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{step === "upload" ? "Upload SPK Document" : "Review SPK Data"}</DialogTitle>
          <DialogDescription>{step === "upload" ? "Upload a PDF or image; AI reads the document structure across insurers." : "Correct every field before saving. Nothing is stored until you confirm."}</DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center transition-colors hover:border-[#3B82F6]">
              {ocrLoading ? (
                <><Loader2 size={28} className="mb-3 animate-spin text-[#3B82F6]" /><span className="text-sm text-gray-600">Reading SPK structure…</span></>
              ) : (
                <><UploadCloud size={28} className="mb-3 text-gray-400" /><span className="text-sm font-medium text-gray-700">Click to upload PDF or image</span><span className="mt-1 text-xs text-gray-400">Supports BCA, Zurich, FPG, MNC and other insurer formats</span></>
              )}
              <input data-testid="spk-file-input" type="file" accept="application/pdf,image/*" className="hidden" disabled={ocrLoading} onChange={(e) => handleFile(e.target.files?.[0])} />
            </label>
            <button data-testid="skip-manual-btn" onClick={() => { setForm(EMPTY); setStep("review"); }} className="w-full text-center text-sm text-gray-500 hover:text-[#111827]">Skip — enter data manually</button>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            {vehMatch?.found && (
              <div data-testid="existing-vehicle-card" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Car size={18} className="shrink-0 text-emerald-700" />
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Existing Vehicle Found · {vehMatch.vehicle.vehicle_id}</div>
                      <div className="text-sm font-semibold text-gray-800">{vehMatch.vehicle.license_plate} · {[vehMatch.vehicle.brand, vehMatch.vehicle.model].filter(Boolean).join(" ") || "—"}</div>
                      <div className="text-xs text-gray-600">{vehMatch.previous_visits} Previous Claim{vehMatch.previous_visits !== 1 ? "s" : ""}{vehMatch.active_claim ? ` · ⚠ Active: ${vehMatch.active_claim.claim_no}` : ""}</div>
                    </div>
                  </div>
                  <Button type="button" variant="outline" size="sm" data-testid="view-vehicle-history-btn" onClick={() => { setOpen(false); reset(); navigate(`/history?vehicle=${vehMatch.vehicle.vehicle_id}`); }}><History size={13} className="mr-1" />View Vehicle History</Button>
                </div>
                <div className="mt-1 text-[11px] text-emerald-700">Data kendaraan terisi otomatis — tetap dapat dikoreksi. Claim BARU tetap dibuat (kendaraan tidak diduplikasi).</div>
              </div>
            )}
            {confirm && (
              <div data-testid="confirm-panel" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
                <h4 className="font-heading text-sm font-bold text-amber-800">⚠ Perlu Konfirmasi — Kendaraan Sudah Terdaftar</h4>
                <div className="mt-2 space-y-1 text-sm text-amber-800">
                  {confirm.warnings.map((w) => <div key={w.code}>• {w.message}</div>)}
                  <div className="text-xs">Kunjungan sebelumnya: <b>{confirm.previous_visits}</b> · Vehicle {confirm.vehicle_id}</div>
                </div>
                <textarea data-testid="confirm-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan membuat claim baru meski ada peringatan (wajib untuk Audit Log)…" className="mt-3 h-16 w-full rounded-md border border-amber-300 bg-white p-2 text-sm outline-none focus:border-amber-500" />
                <div className="mt-2 flex flex-wrap justify-end gap-2">
                  <Button variant="outline" data-testid="confirm-cancel" onClick={() => { setConfirm(null); setReason(""); }}>Batal</Button>
                  {activeWarn && <Button variant="outline" data-testid="confirm-open-active" onClick={() => { setOpen(false); reset(); navigate(`/claims/${activeWarn.claim_id}`); }}>Buka Job Aktif</Button>}
                  <Button data-testid="confirm-create-anyway" disabled={saving} onClick={() => save(true)} className="bg-amber-600 font-semibold text-white hover:bg-amber-700">Buat Claim Baru Tetap</Button>
                </div>
              </div>
            )}

            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 flex items-center justify-between">
              <span>Review and correct every field. Nothing is saved until you confirm.</span>
              {form.spk_file && <span className="flex items-center gap-1 text-emerald-700"><FileText size={13} /> Original attached</span>}
            </div>

            {/* A. CLAIM INFORMATION */}
            <section>
              <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-[#111827]">A. Claim Information</h3>
              <div className="mt-2 grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                <F label="License Plate / Nopol *" testid="f-plate" value={form.license_plate} onChange={(v) => set("license_plate", v.toUpperCase())} />
                <F label="Customer / Insured" testid="f-customer" value={form.customer_name} onChange={(v) => set("customer_name", v)} />
                <F label="Finance / Leasing" testid="f-finance" value={form.finance_name} onChange={(v) => set("finance_name", v)} />
                <F label="Company Customer (if any)" testid="f-company" value={form.company_customer} onChange={(v) => set("company_customer", v)} />
                <BrandModelSelect brand={form.vehicle_brand} model={form.vehicle_model} onChange={(b, m) => setForm((s) => ({ ...s, vehicle_brand: b, vehicle_model: m }))} testidPrefix="spk" />
                <F label="Insurance Company" testid="f-insurer" value={form.insurance_company} onChange={(v) => set("insurance_company", v)} />
                <F label="Insurance Claim Number" testid="f-ins-claim-no" value={form.insurance_claim_number} onChange={(v) => set("insurance_claim_number", v)} />
                <F label="VIN / No. Rangka" testid="f-vin" value={form.vin} onChange={(v) => set("vin", v)} />
                <F label="No. HP Customer" testid="f-phone" value={form.customer_phone} onChange={(v) => set("customer_phone", v)} />
                <F label="Assigned SA" testid="f-sa" value={form.assigned_sa} onChange={(v) => set("assigned_sa", v)} />
                <F label="SPK Number" testid="f-spk-number" value={form.spk_number} onChange={(v) => set("spk_number", v)} />
                <F label="SPK Date" testid="f-spk-date" type="date" value={form.spk_date} onChange={(v) => set("spk_date", v)} />
                <F label="SPK Value (Rp)" testid="f-spk-value" type="number" value={form.spk_value} onChange={(v) => set("spk_value", v)} />
              </div>
            </section>

            {/* B. JASA */}
            <section>
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-[#111827]">B. Jasa / Repair</h3>
                <Button type="button" data-testid="add-jasa-btn" variant="outline" size="sm" className="h-7 text-xs" onClick={addJasa}><Plus size={13} className="mr-1" /> Add Jasa</Button>
              </div>
              <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
                <div className="hidden bg-gray-50 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400 lg:grid lg:grid-cols-[20px_minmax(0,1fr)_110px_48px_92px_50px_92px_88px_24px] lg:gap-2">
                  <span>#</span><span>Description / Panel</span><span>Type</span><span>Qty</span><span>Price List</span><span>Disc%</span><span>Approved</span><span className="text-right">Total</span><span></span>
                </div>
                {form.jasa_items.map((j, i) => (
                  <div key={i} className="border-t border-gray-100 p-2 lg:grid lg:grid-cols-[20px_minmax(0,1fr)_110px_48px_92px_50px_92px_88px_24px] lg:items-center lg:gap-2 lg:p-1.5">
                    <div className="mb-1.5 flex items-center justify-between lg:hidden">
                      <span className="text-[11px] font-semibold text-gray-500">JASA #{i + 1}</span>
                      <button data-testid={`jasa-del-m-${i}`} onClick={() => delJasa(i)} className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                    <span className="hidden text-[11px] font-bold text-gray-400 lg:block">{i + 1}</span>
                    <Input data-testid={`jasa-desc-${i}`} placeholder="Work description / panel" value={j.work_description} onChange={(e) => setJasa(i, "work_description", e.target.value)} className="h-8 text-xs" />
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:mt-0 lg:contents">
                      <SelField testid={`jasa-type-${i}`} value={j.work_type} onChange={(v) => setJasa(i, "work_type", v)} options={WORK_TYPES} />
                      <NumF ph="Qty" value={j.qty} onChange={(v) => setJasa(i, "qty", v)} />
                      <NumF ph="Price List" value={j.price_list} onChange={(v) => setJasa(i, "price_list", v)} />
                      <NumF ph="Disc %" value={j.discount_pct} onChange={(v) => setJasa(i, "discount_pct", v)} />
                      <NumF ph="Approved" value={j.approved_unit_price} onChange={(v) => setJasa(i, "approved_unit_price", v)} />
                      <div className="flex items-center justify-end text-xs font-semibold text-gray-800 lg:pr-1">{formatRupiah(j.total_approved)}</div>
                    </div>
                    <button data-testid={`jasa-del-${i}`} onClick={() => delJasa(i)} className="hidden text-gray-400 hover:text-red-500 lg:flex lg:justify-center"><Trash2 size={14} /></button>
                  </div>
                ))}
                {form.jasa_items.length === 0 && <p className="border-t border-gray-100 p-3 text-center text-xs text-gray-400">No jasa items.</p>}
              </div>
            </section>

            {/* C. SPARE PARTS */}
            <section>
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-[#111827]">C. Spare Parts</h3>
                <Button type="button" data-testid="add-part-btn" variant="outline" size="sm" className="h-7 text-xs" onClick={addPart}><Plus size={13} className="mr-1" /> Add Part</Button>
              </div>
              <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
                <div className="hidden bg-gray-50 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400 lg:grid lg:grid-cols-[20px_minmax(0,1fr)_108px_44px_118px_86px_48px_90px_84px_24px] lg:gap-2">
                  <span>#</span><span>Part Name</span><span>Part No.</span><span>Qty</span><span>Supply</span><span>Price List</span><span>Disc%</span><span>Approved</span><span className="text-right">Total</span><span></span>
                </div>
                {form.spare_parts.map((p, i) => (
                  <div key={i} className="border-t border-gray-100 p-2 lg:grid lg:grid-cols-[20px_minmax(0,1fr)_108px_44px_118px_86px_48px_90px_84px_24px] lg:items-center lg:gap-2 lg:p-1.5">
                    <div className="mb-1.5 flex items-center justify-between lg:hidden">
                      <span className="text-[11px] font-semibold text-gray-500">PART #{i + 1}</span>
                      <button data-testid={`part-del-m-${i}`} onClick={() => delPart(i)} className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                    <span className="hidden text-[11px] font-bold text-gray-400 lg:block">{i + 1}</span>
                    <Input data-testid={`part-name-${i}`} placeholder="Part name / description" value={p.part_name} onChange={(e) => setPart(i, "part_name", e.target.value)} className="h-8 text-xs" />
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:mt-0 lg:contents">
                      <Input data-testid={`part-number-${i}`} placeholder="Part number" value={p.part_number} onChange={(e) => setPart(i, "part_number", e.target.value)} className="h-8 text-xs" />
                      <NumF ph="Qty" value={p.quantity} onChange={(v) => setPart(i, "quantity", v)} />
                      <SelField testid={`part-supply-${i}`} value={p.supply_by} onChange={(v) => setPart(i, "supply_by", v)} options={SUPPLY_BY} />
                      <NumF ph="Price List" value={p.price_list} onChange={(v) => setPart(i, "price_list", v)} />
                      <NumF ph="Disc %" value={p.discount_pct} onChange={(v) => setPart(i, "discount_pct", v)} />
                      <NumF ph="Approved" value={p.approved_unit_price} onChange={(v) => setPart(i, "approved_unit_price", v)} />
                      <div className="flex items-center justify-end text-xs font-semibold text-gray-800 lg:pr-1">{formatRupiah(p.total_approved)}</div>
                    </div>
                    <button data-testid={`part-del-${i}`} onClick={() => delPart(i)} className="hidden text-gray-400 hover:text-red-500 lg:flex lg:justify-center"><Trash2 size={14} /></button>
                  </div>
                ))}
                {form.spare_parts.length === 0 && <p className="border-t border-gray-100 p-3 text-center text-xs text-gray-400">No spare parts.</p>}
              </div>
            </section>

            {/* D. FINANCIAL SUMMARY */}
            <section className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-[#111827]">D. Financial Summary</h3>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-6">
                <Sum label="Approved Jasa" value={approvedJasa} />
                <Sum label="Approved Parts" value={approvedParts} />
                <Sum label="Calculated Total" value={calcTotal} />
                <Sum label="SPK Document Value" value={spkVal} />
                <Sum label="Difference" value={diff} tone={reconOk ? "ok" : "warn"} />
                <div className="flex items-end">
                  <span data-testid="recon-badge" className={`rounded-full px-3 py-1 text-xs font-semibold ${reconOk ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{reconOk ? "MATCH" : "CHECK REQUIRED"}</span>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" data-testid="review-back-btn" onClick={() => setStep("upload")}>Back</Button>
              <Button data-testid="save-claim-btn" disabled={saving} onClick={() => save(false)} className="bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]">{saving ? "Saving…" : "Confirm & Create Claim"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9" />
    </div>
  );
}
function NumF({ ph, value, onChange }) {
  return <Input type="number" min="0" placeholder={ph} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" />;
}
function SelField({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-8 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{label(o)}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function Sum({ label, value, tone }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className={`text-sm font-semibold ${tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-gray-800"}`}>{formatRupiah(value)}</div>
    </div>
  );
}
